import { ScoreDefinitionDTO } from 'business/models/Models';
import React, { useCallback, useEffect, useState } from 'react';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { setExpHitWell } from 'redux/reducers/experimentReducer';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { IStyle } from 'Styles';
import { GetScoreDefn } from 'utils/Global';
import { GeneralObjectType } from '../Contants';
import HeaderContent from './HeaderContent';
import WellIngredients from './WellIngredients';
import WellScores from './WellScores';
import PlateService from 'business/services/PlateService';
import { Plate } from 'components/Experiment/constants';

const WellIngredientsScores = () => {
  const experimentData = useRMSelector(selectExp);
  const dispatch = useRMDispatch();
  const allScores: ScoreDefinitionDTO[] = GetScoreDefn();
  const scoreGroupKey = 'scoreGroupName';
  const scoreGroups: string[] = [
    ...new Set(allScores.map((item) => item[scoreGroupKey])),
  ];
  const [allWellIngsList, setAllWellIngsList] = useState<string[]>([]);
  const [allWellsScoresList, setAllWellsScoresList] = useState<GeneralObjectType[]>([]);
  const [selectedIngredients, setSelectedIngredients] = useState<string[]>([]);
  const [selectedScoreGroups, setSelectedScoreGroups] = useState<GeneralObjectType[]>([]);
  const [highlightedWells, setHighlightedWells] = useState({
    Ingredients: [],
    Scores: [],
  });
  const [alphaBlendedItem, setAlphaBlendedItem] = useState<string>('ing');
  const [lastActionPerformed, setLastActionPerformed] = useState({
    ings: 'or',
    scores: 'or',
  });

  const getIngredientsScoresList = useCallback(async () => {
    let newIngsList: string[] = [];
    const newScoresList = [];

    for (const plate of experimentData.plateDto) {
      for (const well of plate.ingredients.wellList) {
        for (const wellLayer of well.wellLayerList) {
          for (const ingredient of wellLayer.wellLayerIngredientList) {
            newIngsList.push(ingredient.ingredientStockIngredient.longName);
          }
        }
      }
      const plateService = new PlateService();
      const plateScores = await plateService.getPlateScoreBasedOnWellsByPlateId(
        plate.plateId,
      );
      for (const score of plateScores) {
        for (const scoreInfo of score) {
          const groupName = allScores.find(
            (x) =>
              x.wellDropScoreGroupId === scoreInfo.wellDropScoreGroupId &&
              x.wellDropScoreId === scoreInfo.wellDropScoreId,
          );
          newScoresList.push({
            ...scoreInfo,
            scoreGroupName: groupName['scoreGroupName'],
            plateId: plate.plateId,
            plateNumber: plate.plateNumber,
          });
        }
      }
    }
    newIngsList = Array.from(new Set(newIngsList));

    const data = { ingsList: newIngsList, scoresList: newScoresList };
    return data;
  }, [experimentData.plateDto]);

  useEffect(() => {
    const fetch = async () => {
      const newIngScoresData = await getIngredientsScoresList();
      setAllWellIngsList(newIngScoresData?.ingsList);
      setAllWellsScoresList(newIngScoresData?.scoresList);
    };
    fetch();
  }, [experimentData.plateDto]);

  useEffect(() => {
    !selectedScoreGroups.length &&
      !selectedIngredients.length &&
      dispatch(setExpHitWell(undefined));
    !selectedScoreGroups.length &&
      selectedIngredients.length &&
      ingredientsSelection(
        lastActionPerformed.ings,
        selectedIngredients,
        experimentData.plateDto,
      );
    !selectedIngredients.length &&
      selectedScoreGroups.length &&
      scoresSelection(
        lastActionPerformed.scores,
        selectedScoreGroups,
        allWellsScoresList,
      );
  }, [selectedScoreGroups, selectedIngredients]);

  const fillSelectedWellInfo = (
    itemType: string,
    selectedWellsList: GeneralObjectType[],
    well: GeneralObjectType,
  ) => {
    let checkWellEntry: boolean =
      selectedWellsList.findIndex(
        (x) => x.wellNumber === well.wellNumber && x.plateId === well.plateId,
      ) > -1;

    if (checkWellEntry) {
      return !checkWellEntry;
    }

    if (itemType !== alphaBlendedItem) {
      const alreadySelectedWells =
        alphaBlendedItem === 'ing'
          ? highlightedWells.Ingredients
          : highlightedWells.Scores;
      checkWellEntry =
        alreadySelectedWells && alreadySelectedWells.length > 0
          ? alreadySelectedWells.findIndex(
              (x) => x.wellNumber === well.wellNumber && x.plateId === well.plateId,
            ) > -1
          : true;
      return checkWellEntry;
    } else {
      const alreadySelectedWells =
        itemType === 'ing' ? highlightedWells.Scores : highlightedWells.Ingredients;
      checkWellEntry =
        alreadySelectedWells && alreadySelectedWells.length > 0
          ? alreadySelectedWells.findIndex(
              (x) => x.wellNumber === well.wellNumber && x.plateId === well.plateId,
            ) > -1
          : true;
      return checkWellEntry;
    }
  };

  const ingredientsSelection = (
    actionType: string,
    selectedIngredients: string[],
    platelist: Plate[],
  ) => {
    if (!selectedIngredients.length) {
      setHighlightedWells({ ...highlightedWells, Ingredients: [] });
      return;
    }
    const selectedWellsList: GeneralObjectType[] = [];
    const ingredientsSelectedWells: GeneralObjectType[] = [];

    for (const plate of platelist) {
      for (const well of plate.ingredients.wellList) {
        const allIngsPerWell: string[] = [];
        for (const wellLayer of well.wellLayerList) {
          for (const ingredient of wellLayer.wellLayerIngredientList) {
            allIngsPerWell.push(ingredient.ingredientStockIngredient.longName);
          }
        }
        let ing;
        if (actionType === 'or') {
          ing = selectedIngredients.some((element) => allIngsPerWell.includes(element));
          setLastActionPerformed({ ...lastActionPerformed, ings: 'or' });
        } else if (actionType === 'and') {
          ing = selectedIngredients.every((element) => allIngsPerWell.includes(element));
          setLastActionPerformed({ ...lastActionPerformed, ings: 'and' });
        }
        if (ing) {
          const well1: GeneralObjectType = {
            wellNumber: well.wellNumber,
            plateId: plate.plateId,
            hit: true,
          };
          ingredientsSelectedWells.push(well1);
          if (fillSelectedWellInfo('ing', selectedWellsList, well1)) {
            selectedWellsList.push(well1);
          }
        }
      }
    }
    setAlphaBlendedItem('ing');
    setHighlightedWells({ ...highlightedWells, Ingredients: ingredientsSelectedWells });
    dispatch(setExpHitWell(selectedWellsList));
  };

  const scoresSelection = (actionType, selectedScoreGroups, allWellsScoresList) => {
    if (!selectedScoreGroups.length) {
      setHighlightedWells({ ...highlightedWells, Scores: [] });
      return;
    }
    const selectedWellsList: GeneralObjectType[] = [];
    const scoresSelectedWells: GeneralObjectType[] = [];
    const newScoreObject = {};

    for (const wellScore of allWellsScoresList) {
      if (!newScoreObject[wellScore.wellNumber]) {
        newScoreObject[wellScore.wellNumber] = [];
      }
      newScoreObject[wellScore.wellNumber].push(wellScore);
    }
    for (const scoreWellNumber in newScoreObject) {
      let findWellScore;

      if (actionType === 'or') {
        findWellScore = [...selectedScoreGroups]
          .filter((x) => x.wellNumber == scoreWellNumber)
          .some((element) =>
            newScoreObject[scoreWellNumber].find(
              (x) =>
                x.wellDropScoreGroupId === element.wellDropScoreGroupId &&
                x.wellDropScoreId === element.wellDropScoreId,
            ),
          );
        setLastActionPerformed({ ...lastActionPerformed, scores: 'or' });
      } else if (actionType === 'and') {
        findWellScore = selectedScoreGroups.every((element) =>
          newScoreObject[scoreWellNumber].find(
            (x) =>
              x.wellDropScoreGroupId === element.wellDropScoreGroupId &&
              x.wellDropScoreId === element.wellDropScoreId,
          ),
        );
        setLastActionPerformed({ ...lastActionPerformed, scores: 'and' });
      }
      if (findWellScore) {
        const well1: GeneralObjectType = {
          wellNumber: newScoreObject[scoreWellNumber][0].wellNumber,
          plateId: newScoreObject[scoreWellNumber][0].plateId,
          hit: true,
        };
        scoresSelectedWells.push(well1);
        if (fillSelectedWellInfo('score', selectedWellsList, well1)) {
          selectedWellsList.push(well1);
        }
      }
    }
    setAlphaBlendedItem('score');
    setHighlightedWells({ ...highlightedWells, Scores: scoresSelectedWells });
    dispatch(setExpHitWell(selectedWellsList));
  };
  return (
    <div id='exp-finddrop-index-wrapper'>
      <div id='exp-finddrop-index-ingredientssection' style={styles.IngredientsSection}>
        <HeaderContent
          title="Ingredient Name"
          lastActionPerformed={lastActionPerformed?.ings}
          enableDelete={selectedIngredients.length > 0}
          onClickOR={() =>
            ingredientsSelection('or', selectedIngredients, experimentData.plateDto)
          }
          onClickAND={() =>
            ingredientsSelection('and', selectedIngredients, experimentData.plateDto)
          }
          onClickDelete={() => setSelectedIngredients([])}
        />
        <div id='exp-finddrop-index-ingredientschildsection' style={styles.ChildSectionIngredients}>
          {
            <WellIngredients
              allWellIngsList={allWellIngsList}
              selectedIngredients={selectedIngredients}
              setSelectedIngredients={setSelectedIngredients}
              ingredientsSelection={ingredientsSelection}
              lastActionPerformed={lastActionPerformed}
            />
          }
        </div>
      </div>
      {!window?.location?.pathname.includes('/screen') && (
        <div id='exp-finddrop-index-scoresection' style={styles.ScoresSection}>
          <HeaderContent
            title="Score Name"
            lastActionPerformed={lastActionPerformed?.scores}
            enableDelete={selectedScoreGroups.length > 0}
            onClickOR={() =>
              scoresSelection('or', selectedScoreGroups, allWellsScoresList)
            }
            onClickAND={() =>
              scoresSelection('and', selectedScoreGroups, allWellsScoresList)
            }
            onClickDelete={() => setSelectedScoreGroups([])}
          />
          <div id='exp-finddrop-index-scorechildsection' style={styles.ChildSectionScores}>
            {
              <WellScores
                scoreGroups={scoreGroups}
                allWellsScoresList={allWellsScoresList}
                allScores={allScores}
                selectedScoreGroups={selectedScoreGroups}
                setSelectedScoreGroups={setSelectedScoreGroups}
                ingredientsSelection={ingredientsSelection}
                scoresSelection={scoresSelection}
                lastActionPerformed={lastActionPerformed}
              />
            }
          </div>
        </div>
      )}
    </div>
  );
};

export const styles: IStyle = {
  IngredientsSection: {
    height: window?.location?.pathname.includes('/screen') ? '82vh' : '35vh',
  },
  ScoresSection: {
    height: '47vh',
  },
  ChildSectionIngredients: {
    height: '85%',
    overflow: 'auto',
  },
  ChildSectionScores: {
    height: '86%',
    overflow: 'auto',
  },
};
export default WellIngredientsScores;
